import { Star } from "lucide-react";

import styles from "./_star-row.module.css";

/**
 * The guide's `.rt` — five stars, filled to the score EXACTLY: whole stars,
 * then a partial one for the remainder. 4.7 is four filled and a fifth filled
 * 70% across.
 *
 * This has now been wrong in both directions, which is why it is worth the
 * extra element.
 *
 * `Math.round` came first: it drew FIVE filled stars for 4.6 — the mark of a
 * flawless tour — and rendered every score from 4.5 to 5.0 identically, on the
 * one graphic a traveller reads as the verdict.
 *
 * `Math.floor` replaced it and overcorrected: 4.9 drew four stars and an empty
 * one, understating a tour by a whole star, and the prototype draws five at
 * 4.7. Neither whole-star rule can be right, because the thing being shown is
 * not a whole number.
 *
 * A partial fill is what both wanted: it reads as "nearly five" the way the
 * prototype does, it never claims a star the score has not earned, and 4.5 and
 * 5.0 stop looking the same.
 *
 * Neither a server nor a client component on purpose: it holds no state and
 * reads no request, so the booking rail (client) and the reviews summary
 * (server) can both render it. Two copies is how their star sizes and their
 * empty-star colour would drift apart on the same page.
 *
 * The empty stars are the SAME amber as the filled ones, drawn as an outline
 * (`_star-row.module.css`). A grey track made the row read as two different
 * controls side by side; one colour makes it read as one rating filled to a
 * level. `aria-hidden` on every star because the score is stated in text
 * beside it — a screen reader announcing five graphics before the number is
 * noise, not information, and it is also why the outline does not owe the 3:1
 * non-text contrast floor.
 */
export function StarRow({
  average,
  size = "md",
}: {
  average: number;
  /** Per-star size. The guide runs 16px in the reviews summary (`.rt`) and
   *  14px in the booking rail (`.rt-sm`), with a 2px / 3px gap. */
  size?: "md" | "sm";
}) {
  const clamped = Math.min(Math.max(average, 0), 5);
  const filled = Math.floor(clamped);
  // Sub-1% remainders render as a sliver nobody can read as a rating, so the
  // star stays empty rather than growing a smudge on its left edge.
  const partialPercent = Math.round((clamped - filled) * 100);
  return (
    <span className={styles.row} data-size={size}>
      {[1, 2, 3, 4, 5].map((i) => {
        if (i <= filled) {
          return (
            <Star
              key={i}
              aria-hidden
              className={`${styles.star} ${styles.filled}`}
              strokeWidth={1.5}
            />
          );
        }
        if (i === filled + 1 && partialPercent > 0) {
          return <PartialStar key={i} percent={partialPercent} />;
        }
        return (
          <Star
            key={i}
            aria-hidden
            className={`${styles.star} ${styles.empty}`}
            strokeWidth={1.5}
          />
        );
      })}
    </span>
  );
}

/**
 * One star filled part of the way across.
 *
 * TWO SLICES OF THE SAME GLYPH, SIDE BY SIDE — the earned part filled, the
 * remainder an outline — and NEITHER IS DRAWN OVER THE OTHER. They are still
 * the same glyph at the same size, so they cannot drift.
 *
 * It was a full filled star laid over a full outlined one, which looks
 * identical in principle and is not: both are stroked, a stroke anti-aliases,
 * and so the one underneath bled through the other's soft edges for the whole
 * of the overlap. The star carrying the remainder wore a dirty rim its four
 * solid neighbours did not have, which is what got reported.
 *
 * The widths are inline styles because they are DATA — a different number per
 * rating — not a design decision; there is no token for "70% of a star".
 */
function PartialStar({ percent }: { percent: number }) {
  return (
    <span aria-hidden className={styles.partial}>
      <span
        className={styles.clipRemainder}
        style={{ width: `${100 - percent}%` }}
      >
        <Star className={`${styles.star} ${styles.empty}`} strokeWidth={1.5} />
      </span>
      <span className={styles.clip} style={{ width: `${percent}%` }}>
        <Star className={`${styles.star} ${styles.filled}`} strokeWidth={1.5} />
      </span>
    </span>
  );
}
